import { useCallback } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { markdown } from '@codemirror/lang-markdown'
import { oneDark } from '@codemirror/theme-one-dark'
import { EditorView } from '@codemirror/view'

interface PromptEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  minHeight?: string
  maxHeight?: string
  readOnly?: boolean
}

// Custom theme to match ARCHON's design
const archonTheme = EditorView.theme({
  '&': {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: '0.75rem',
    border: '1px solid rgba(107, 114, 128, 0.3)',
    fontSize: '14px',
  },
  '&.cm-focused': {
    outline: 'none',
    borderColor: 'rgba(99, 102, 241, 0.5)',
    boxShadow: '0 0 0 2px rgba(99, 102, 241, 0.1)',
  },
  '.cm-gutters': {
    backgroundColor: 'transparent',
    borderRight: '1px solid rgba(107, 114, 128, 0.2)',
    color: 'rgb(107, 114, 128)',
  },
  '.cm-activeLineGutter': {
    backgroundColor: 'rgba(99, 102, 241, 0.1)',
  },
  '.cm-activeLine': {
    backgroundColor: 'rgba(99, 102, 241, 0.05)',
  },
  '.cm-content': {
    padding: '12px 16px',
    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
  },
  '.cm-line': {
    padding: '0 4px',
  },
  '.cm-placeholder': {
    color: 'rgb(107, 114, 128)',
    fontStyle: 'italic',
  },
  '.cm-scroller': {
    overflow: 'auto',
  },
})

export function PromptEditor({
  value,
  onChange,
  placeholder = 'Digite o system prompt do agente...',
  minHeight = '200px',
  maxHeight = '400px',
  readOnly = false,
}: PromptEditorProps) {
  const handleChange = useCallback(
    (val: string) => {
      onChange(val)
    },
    [onChange]
  )

  return (
    <div className="prompt-editor-container">
      <CodeMirror
        value={value}
        onChange={handleChange}
        extensions={[markdown(), archonTheme]}
        theme={oneDark}
        placeholder={placeholder}
        readOnly={readOnly}
        basicSetup={{
          lineNumbers: true,
          highlightActiveLineGutter: true,
          highlightActiveLine: true,
          foldGutter: true,
          dropCursor: true,
          allowMultipleSelections: true,
          indentOnInput: true,
          bracketMatching: true,
          closeBrackets: true,
          autocompletion: false,
          rectangularSelection: true,
          crosshairCursor: false,
          highlightSelectionMatches: true,
        }}
        style={{
          minHeight,
          maxHeight,
          overflow: 'auto',
        }}
      />
      <style>{`
        .prompt-editor-container .cm-editor {
          min-height: ${minHeight};
          max-height: ${maxHeight};
        }
        .prompt-editor-container .cm-scroller {
          min-height: ${minHeight};
          max-height: ${maxHeight};
        }
      `}</style>
    </div>
  )
}
